// Home page "Our Services" content: three service lines, each with three tabs.

export interface HomeServiceTab {
  id: string;
  label: string;
  paragraphs: string[];
}

export interface HomeService {
  id: string;
  name: string;
  tabs: HomeServiceTab[];
}

export const HOME_SERVICES: HomeService[] = [
  {
    id: 'autonomous',
    name: 'Autonomous Agent Development',
    tabs: [
      {
        id: 'workflow',
        label: 'Workflow Automation',
        paragraphs: [
          'Operations run on repeatable, multi-step work. We build autonomous agents that own those workflows end to end: they read context from your systems, plan the steps, call the right tools and APIs, and complete the task. Unlike scripted automation, agents reason through exceptions, adapt when inputs change and escalate to a person when a decision needs judgment.',
          'Every agent ships with scoped permissions, human-in-the-loop approvals for sensitive actions, full action logs and evaluation suites that gate each release. The result is automation you can trust in production, integrated with your existing applications through secure APIs.',
        ],
      },
      {
        id: 'domain',
        label: 'Domain-Specific Agents',
        paragraphs: [
          'General-purpose models become valuable when they are specialized. We build domain agents for finance, healthcare, pharma, education and other functions, equipped with the tools, data access and business rules of that domain.',
          'Each agent is grounded in your trusted data and knowledge sources, uses retrieval and tool calling to act with context, and is measured against task-level benchmarks built from your real cases, so you can see accuracy, cost and latency before and after launch.',
        ],
      },
      {
        id: 'pipeline',
        label: 'Autonomous Data Pipeline',
        paragraphs: [
          'Reliable decisions start with reliable data. We build self-monitoring data pipelines in which agents detect schema drift, validate quality, repair failures and document changes, keeping data flowing on schedule across real-time, batch and hybrid workloads.',
          'Lineage and observability are built in, so every figure can be traced to its source, and downstream analytics, machine learning and agents always work from governed, current data.',
        ],
      },
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise AI & Automation Consulting',
    tabs: [
      {
        id: 'strategy',
        label: 'Strategy Development',
        paragraphs: [
          'We work with leadership teams to define an AI and automation strategy tied to business outcomes: where agents and intelligence will create value, what data and platform foundations they need, and how risk, security and compliance are managed.',
          'Deliverables are practical: a prioritized roadmap, target architecture, build-versus-buy decisions, operating model and success metrics, so strategy turns into funded, measurable delivery.',
        ],
      },
      {
        id: 'usecase',
        label: 'Use Case Identification',
        paragraphs: [
          'We map your processes and data to find the automation and agent opportunities with the highest return and the lowest risk. Each candidate is scored on business value, feasibility, data readiness and risk.',
          'You receive a ranked portfolio with business cases, so teams can agree where to start and which pilots to fund first.',
        ],
      },
      {
        id: 'feasibility',
        label: 'Feasibility Assessments',
        paragraphs: [
          'Before budget is committed, we validate that a proposed AI initiative will work: technical feasibility, integration complexity, data quality, security requirements and expected return, tested with rapid prototypes on your own data.',
          'Findings come with assumptions, confidence levels and a clear go, adjust or stop recommendation.',
        ],
      },
    ],
  },
  {
    id: 'orchestration',
    name: 'Agentic Orchestration & Decision Intelligence',
    tabs: [
      {
        id: 'orchestrators',
        label: 'Agentic Orchestrators',
        paragraphs: [
          'Single agents solve tasks; orchestrators run the business process. We design agentic orchestrators that plan work, delegate it to specialist agents, coordinate tools and data sources, and recover from failures across long-running, multi-step operations.',
          'Built on open, interoperable standards such as MCP and REST or event-driven APIs, our orchestration layer is model-agnostic and keeps state and context reliably, so you can change models and add agents without rebuilding the system.',
        ],
      },
      {
        id: 'decisions',
        label: 'Data Decision Pipelines',
        paragraphs: [
          'We turn raw signals into decisions. Data decision pipelines ingest data from your systems, enrich and score it with machine learning and agents, and route recommended actions to the right system or person in real time.',
          'Each decision is explainable and traceable: inputs, rules, model versions and approvals are recorded, so teams can audit outcomes and keep improving them.',
        ],
      },
      {
        id: 'governance',
        label: 'Agent Observability & Governance',
        paragraphs: [
          'Production agents need the same rigor as any critical software. We instrument agents with tracing, evaluation and cost monitoring, and put guardrails around data access, actions and outputs.',
          'Continuous evaluation on live traffic, drift and regression alerts, audit trails and policy controls help you scale agents with confidence and meet security and compliance expectations.',
        ],
      },
    ],
  },
];
